import { useCallback, useEffect, useState } from "react";
import { fetchTreasuryChannels } from "./treasuryApi";

/**
 * Normalizes one currency row into safe numbers.
 * @param {Object} row - Raw currency position from the API
 * @returns {Object}
 */
function normalizeRow(row) {
    return {
        currency: row.currency || "",
        channel: row.channel || "stripe",
        collected: Number(row.collected) || 0,
        paid_out: Number(row.paid_out) || 0,
        available: Number(row.available) || 0,
        artist_obligations: Number(row.artist_obligations) || 0,
        pending_payouts: Number(row.pending_payouts) || 0,
        coverage: Number(row.coverage) || 0,
    };
}

/**
 * Normalizes the API response into a report object with safe defaults.
 * @param {*} data - Decrypted API payload
 * @returns {Object|null}
 */
function normalizeReport(data) {
    if (!data || typeof data !== "object") return null;
    return {
        generatedAt: data.generated_at || null,
        currencies: Array.isArray(data.currencies) ? data.currencies.map(normalizeRow) : [],
        channels: data.channels && typeof data.channels === "object" ? data.channels : {},
        crossCurrency: Array.isArray(data.cross_currency) ? data.cross_currency : [],
    };
}

/**
 * Runs the GET request inside an abort-aware wrapper.
 * @param {Object} args - Loader arguments
 */
async function loadChannels({ signal, setReport, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchTreasuryChannels();
        if (!signal.aborted) setReport(normalizeReport(data));
    } catch (err) {
        if (!signal.aborted) setError(err?.response?.data || "Impossible de charger la trésorerie");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook fetching the per-channel treasury position.
 * @returns {{ report: Object|null, loading: boolean, error: any, refresh: Function }}
 */
function useTreasuryChannels() {
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    const refresh = useCallback(() => setReloadToken((token) => token + 1), []);

    useEffect(() => {
        const controller = new AbortController();
        loadChannels({ signal: controller.signal, setReport, setError, setLoading });
        return () => controller.abort();
    }, [reloadToken]);

    return { report, loading, error, refresh };
}

export default useTreasuryChannels;
